import React, { Suspense, useEffect, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Field } from '@atlaskit/form';
import Select, { components } from '@atlaskit/select';
import { logException } from '@atlaskit/teams-client/sentry';
import { PlatformSiteAri } from '@atlassian/ari';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { messages } from '../i18n/messages';

import type { TeamTypeSelectQuery } from './__generated__/TeamTypeSelectQuery.graphql';
import { TeamTypeOption, type TeamTypeOptionData } from './team-type-option/TeamTypeOption';

const styles = cssMap({
	menu: {
		width: '300px',
	},
});

export type TeamTypeValue = {
	id: string;
	name: string;
	verified?: boolean;
};

export type TeamTypeSelectProps = {
	cloudId: string;
	onChange: (value: TeamTypeValue) => void;
};

const TeamTypeSelectInternal = ({ cloudId, onChange }: TeamTypeSelectProps) => {
	const scopeId = PlatformSiteAri.create({ siteId: cloudId }).toString();

	const data = useLazyLoadQuery<TeamTypeSelectQuery>(
		graphql`
			query TeamTypeSelectQuery($scopeId: ID!) {
				team {
					typesWithinScope(scopeId: $scopeId) @optIn(to: "Team-types") {
						nodes {
							id
							verified
							name
							description
							default
						}
					}
				}
			}
		`,
		{ scopeId },
	);

	const teamTypeOptions: TeamTypeOptionData[] = useMemo(() => {
		const nodes = data?.team?.typesWithinScope?.nodes;
		if (!nodes) {
			return [];
		}

		return nodes
			.filter((node) => node !== null)
			.map((node) => ({
				label: node.name,
				value: node.id,
				data: {
					name: node.name,
					verified: node.verified ?? false,
					default: !!node.default,
					description: node.description ?? '',
				},
			}));
	}, [data]);

	const defaultTeamType = useMemo(() => {
		const nodes = data?.team?.typesWithinScope?.nodes;
		if (!nodes) {
			return undefined;
		}

		// Verified default team type is the one we need
		return nodes.find((node) => node.default === 'VERIFIED') ?? nodes[0];
	}, [data]);

	// This will help with the check for MEMBERSHIP CONTROL section after the default team type is populated
	useEffect(() => {
		if (defaultTeamType) {
			onChange({
				id: defaultTeamType.id,
				name: defaultTeamType.name,
				verified: defaultTeamType.verified ?? false,
			});
		} else {
			// Suspense will make sure the component is always rendered after the data is loaded,
			// so it's safe to check the undefined default team type and call onChange with an empty value, to hide the field
			onChange({
				id: '',
				name: '',
				verified: false,
			});
		}
	}, [onChange, defaultTeamType]);

	return (
		<Field
			name="teamType"
			isRequired
			label={<FormattedMessage {...messages.teamTypeSelectLabel} />}
		>
			{() => (
				<Select<TeamTypeOptionData>
					inputId="team-create-team-type-select"
					options={teamTypeOptions}
					defaultValue={
						defaultTeamType && {
							label: defaultTeamType.name,
							value: defaultTeamType.id,
							data: {
								name: defaultTeamType.name,
								verified: defaultTeamType.verified ?? false,
								default: !!defaultTeamType.default,
								description: defaultTeamType.description ?? '',
							},
						}
					}
					components={{
						Option: TeamTypeOption,
						Menu: (props) => <components.Menu {...props} xcss={styles.menu} />,
					}}
					onChange={(newValue) => {
						if (onChange && newValue) {
							const newTeamType: TeamTypeValue = {
								id: newValue.value ?? '',
								name: newValue.label ?? '',
								verified: newValue.data?.verified ?? false,
							};
							onChange(newTeamType);
						}
					}}
					isRequired
				/>
			)}
		</Field>
	);
};

export const TeamTypeSelect = (props: TeamTypeSelectProps) => {
	return (
		<ErrorBoundary
			fallback={null}
			onError={(error) => {
				logException(error, 'TeamTypeSelectErrorBoundary');
			}}
		>
			<Suspense
				fallback={
					<Field
						name="teamType"
						isRequired
						label={<FormattedMessage {...messages.teamTypeSelectLabel} />}
					>
						{() => <Select isLoading />}
					</Field>
				}
			>
				<RelayEnvironmentProvider>
					<TeamTypeSelectInternal {...props} />
				</RelayEnvironmentProvider>
			</Suspense>
		</ErrorBoundary>
	);
};
